import { RefreshTokenRejectedError, RefreshUnavailableError } from './errors';

/**
 * Auth session model shared by every platform — the shape of OnEarly `app`'s
 * `Authentication` singleton, behind an interface:
 *
 * - the access token lives in memory only; what survives a restart is the refresh
 *   token (+ id token), so a leaked web storage never yields a usable bearer;
 * - refresh is single-flight in-process and can be serialised across tabs (Ory rotates
 *   refresh tokens with reuse detection: replaying one revokes the whole family);
 * - a rejected refresh surfaces as `expired`, never as a silent downgrade to anonymous;
 *   an unavailable refresh (network, 5xx) leaves the session alone.
 */
export type SessionStatus = 'unknown' | 'anonymous' | 'authenticated' | 'expired';

export interface Tokens {
  accessToken: string;
  refreshToken: string | undefined;
  /** Epoch milliseconds. */
  expiresAt: number | undefined;
  idToken?: string;
  /**
   * The Ory client Core minted the tokens for: refresh and revoke with it. Absent for a
   * session from before Core named it, which is the OnEarly app's client (core PRD-2355).
   */
  clientId?: string;
}

export interface Session {
  status: SessionStatus;
  tokens: Tokens | undefined;
}

/** What is persisted between launches — never the access token. */
export interface PersistedSession {
  refreshToken: string;
  idToken?: string;
  /** The client the refresh token belongs to; a refresh with any other fails. */
  clientId?: string;
}

/** What to persist for a session: its refresh token, ID token and client, nothing else. */
function persistable(refreshToken: string, tokens: Tokens): PersistedSession {
  return {
    refreshToken,
    idToken: tokens.idToken,
    ...(tokens.clientId ? { clientId: tokens.clientId } : {}),
  };
}

/** Persisted store — SecureStore on native, `localStorage` on web (ADR 0003). */
export interface TokenStorage {
  load(): Promise<PersistedSession | undefined>;
  save(session: PersistedSession): Promise<void>;
  clear(): Promise<void>;
}

export type SocialProvider = 'apple' | 'google';
export type IdentifierChannel = 'email' | 'phone';
export type FlowKind = 'login' | 'registration' | 'recovery';
export type AuthMethod = 'code' | 'password';

/**
 * Core's `NativeAuthFlowState`, kept whole: `/login/verify` and `/login/resend` want it
 * echoed back verbatim (plus the code), not just the flow id.
 */
export interface LoginFlow {
  flowId: string;
  flowKind: FlowKind;
  csrfToken: string;
  identifier: string;
  channel: IdentifierChannel;
  /** ISO date-time. */
  expiresAt: string;
  authMethod?: AuthMethod;
  sessionTokenExchangeCode?: string;
  /** Ory trait names (`phone_number`, `name.first`) — the one snake_case corner of the API. */
  registrationTraits?: {
    email: string;
    phone_number: string;
    name: { first: string; last: string };
  };
  /**
   * True when the account has no home region yet: a new one, or one an invitation made. The
   * app then shows where it will be kept (`region.ts`). Absent from a Core that predates regions.
   */
  homeRegionRequired?: boolean;
  /**
   * Where the account is to be kept: the region the code screen names, pre-selected or picked
   * under "Wrong region?". It rides back to `/login/verify`, which records it for an account
   * with no home yet (core#721).
   */
  homeRegion?: string;
}

/** What starts a code sign-in. `homeRegion` is where a new account is kept (`region.ts`). */
export interface StartLogin {
  channel: IdentifierChannel;
  identifier: string;
  homeRegion?: string;
}

export interface SocialLoginStart {
  /** Browser URL straight to the provider; the app opens it and waits for `redirectUri`. */
  url: string;
  /** Pair with the `code` the provider sends back to finish at `/login/social/complete`. */
  sessionTokenExchangeCode: string;
}

/**
 * The seam every auth backend implements. `OnEarlyAuthProvider` (Core-brokered
 * phone/email codes, password, Apple/Google) is the real one; `FakeAuthProvider` is for
 * local development and tests.
 */
export interface AuthProvider {
  startLogin(input: StartLogin): Promise<LoginFlow>;
  resendLogin(flow: LoginFlow): Promise<LoginFlow>;
  verifyLogin(input: { flow: LoginFlow; code: string }): Promise<Tokens>;
  passwordLogin(input: { identifier: string; password: string }): Promise<Tokens>;
  startSocialLogin(input: {
    provider: SocialProvider;
    redirectUri: string;
  }): Promise<SocialLoginStart>;
  completeSocialLogin(input: {
    sessionTokenExchangeCode: string;
    returnToCode: string;
  }): Promise<Tokens>;
  /** The provider's email already belongs to an account: confirm it with a one-time code. */
  continueSocialLogin(input: { flowId: string }): Promise<LoginFlow>;
  refresh(tokens: Tokens): Promise<Tokens>;
  signOut(tokens: Tokens | undefined): Promise<void>;
}

type Listener = (session: Session) => void;

export interface SessionManagerOptions {
  provider: AuthProvider;
  storage: TokenStorage;
  /** Refresh this many ms before `expiresAt` (app: 3 minutes). */
  refreshSkewMs?: number;
  /** Pause between refresh attempts; the last value is retried once more (app: 0s, 1s, 2s). */
  retryDelaysMs?: readonly number[];
  /** Serialise refreshes across tabs/processes (Web Locks on web); identity by default. */
  withLock?: <T>(work: () => Promise<T>) => Promise<T>;
  now?: () => number;
  sleep?: (ms: number) => Promise<void>;
}

const DEFAULT_RETRY_DELAYS_MS = [0, 1_000, 2_000] as const;

/** At most one forced refresh (`refreshNow`) in this long, per signed-in session. */
const FORCED_REFRESH_COOLDOWN_MS = 30_000;

export class SessionManager {
  private session: Session = { status: 'unknown', tokens: undefined };
  private readonly listeners = new Set<Listener>();
  private inflightRefresh: Promise<Tokens> | undefined;
  private lastForcedRefreshAt = Number.NEGATIVE_INFINITY;
  /** The auth backend — screens drive login flows through it. */
  readonly provider: AuthProvider;
  private readonly storage: TokenStorage;
  private readonly refreshSkewMs: number;
  private readonly retryDelaysMs: readonly number[];
  private readonly withLock: <T>(work: () => Promise<T>) => Promise<T>;
  private readonly now: () => number;
  private readonly sleep: (ms: number) => Promise<void>;

  constructor(options: SessionManagerOptions) {
    this.provider = options.provider;
    this.storage = options.storage;
    this.refreshSkewMs = options.refreshSkewMs ?? 3 * 60_000;
    this.retryDelaysMs = options.retryDelaysMs ?? DEFAULT_RETRY_DELAYS_MS;
    this.withLock = options.withLock ?? ((work) => work());
    this.now = options.now ?? (() => Date.now());
    this.sleep = options.sleep ?? ((ms) => new Promise((resolve) => setTimeout(resolve, ms)));
  }

  getSession(): Session {
    return this.session;
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Load the persisted refresh token; call once at app start. The session counts as
   * authenticated straight away (the person has not signed out), and the first request
   * mints an access token through `getAccessToken()`.
   */
  async hydrate(): Promise<Session> {
    const persisted = await this.storage.load();
    this.set(
      persisted
        ? {
            status: 'authenticated',
            tokens: {
              accessToken: '',
              refreshToken: persisted.refreshToken,
              expiresAt: 0,
              idToken: persisted.idToken,
              clientId: persisted.clientId,
            },
          }
        : { status: 'anonymous', tokens: undefined },
    );
    return this.session;
  }

  async signIn(tokens: Tokens): Promise<void> {
    this.lastForcedRefreshAt = Number.NEGATIVE_INFINITY;
    if (tokens.refreshToken) {
      await this.storage.save(persistable(tokens.refreshToken, tokens));
    } else {
      await this.storage.clear();
    }
    this.set({ status: 'authenticated', tokens });
  }

  /** Local state goes first; server-side revocation is best effort (app does the same). */
  async signOut(): Promise<void> {
    const tokens = this.session.tokens;
    await this.storage.clear();
    this.set({ status: 'anonymous', tokens: undefined });
    await this.provider.signOut(tokens).catch(() => undefined);
  }

  /**
   * A valid access token, refreshing single-flight when it is missing or about to expire.
   * Resolves `undefined` for anonymous and expired sessions; throws
   * `RefreshUnavailableError` when the session is fine but Ory could not be reached.
   */
  async getAccessToken(): Promise<string | undefined> {
    const tokens = this.session.tokens;
    if (!tokens || this.session.status === 'expired') return undefined;
    // A refresh under way supersedes the token in hand, however fresh: after a 421 it carries
    // the wrong home claim, and `refreshNow` is replacing it.
    if (!this.inflightRefresh) {
      if (tokens.accessToken && !this.isExpiringSoon(tokens)) return tokens.accessToken;
      if (!tokens.refreshToken) {
        this.set({ status: 'expired', tokens });
        return undefined;
      }
    }
    try {
      return (await this.refreshing(tokens)).accessToken;
    } catch (error) {
      if (error instanceof RefreshTokenRejectedError) return undefined;
      throw error;
    }
  }

  /**
   * Refresh now, whatever the access token's expiry: Core answered `421 WRONG_REGION`, so the
   * token's home-region claim is missing or stale (platform multi-region edge.md 3.4). Joins a
   * refresh already under way; a rejected refresh ends the session as any refresh does.
   * At most once in 30 seconds: where a refresh can't fix the cause (a build pinned to another
   * region's name), every 421 would otherwise rotate the refresh token. The OE app does the same.
   */
  async refreshNow(): Promise<void> {
    const tokens = this.session.tokens;
    if (!tokens?.refreshToken || this.session.status !== 'authenticated') return;
    if (!this.inflightRefresh) {
      if (this.now() - this.lastForcedRefreshAt < FORCED_REFRESH_COOLDOWN_MS) return;
      this.lastForcedRefreshAt = this.now();
    }
    try {
      await this.refreshing(tokens);
    } catch (error) {
      if (!(error instanceof RefreshTokenRejectedError)) throw error;
    }
  }

  /** The refresh under way, or a new one: one at a time, so a refresh token is spent once. */
  private refreshing(tokens: Tokens): Promise<Tokens> {
    this.inflightRefresh ??= this.refreshWithRetry(tokens).finally(() => {
      this.inflightRefresh = undefined;
    });
    return this.inflightRefresh;
  }

  private async refreshWithRetry(tokens: Tokens): Promise<Tokens> {
    let lastError: unknown;
    for (let attempt = 0; attempt < this.retryDelaysMs.length; attempt += 1) {
      const delay = this.retryDelaysMs[attempt] ?? 0;
      if (delay > 0) await this.sleep(delay + Math.random() * 250);
      try {
        return await this.withLock(() => this.refreshOnce(tokens));
      } catch (error) {
        if (error instanceof RefreshTokenRejectedError) {
          // The grant is dead: drop the persisted token, keep the session visible as expired.
          await this.storage.clear();
          this.set({ status: 'expired', tokens });
          throw error;
        }
        lastError = error;
      }
    }
    throw lastError instanceof RefreshUnavailableError
      ? lastError
      : new RefreshUnavailableError(undefined, lastError);
  }

  private async refreshOnce(tokens: Tokens): Promise<Tokens> {
    // Inside the lock: another tab may have rotated the refresh token — spend the freshest.
    const persisted = await this.storage.load();
    const current =
      persisted && persisted.refreshToken !== tokens.refreshToken
        ? {
            ...tokens,
            refreshToken: persisted.refreshToken,
            idToken: persisted.idToken,
            clientId: persisted.clientId,
          }
        : tokens;
    const next = await this.provider.refresh(current);
    if (next.refreshToken) {
      await this.storage.save(persistable(next.refreshToken, next));
    }
    this.set({ status: 'authenticated', tokens: next });
    return next;
  }

  private isExpiringSoon(tokens: Tokens): boolean {
    return tokens.expiresAt !== undefined && tokens.expiresAt - this.refreshSkewMs <= this.now();
  }

  private set(session: Session): void {
    this.session = session;
    for (const listener of this.listeners) listener(session);
  }
}

/** In-memory storage — tests and the web dev server. */
export class MemoryTokenStorage implements TokenStorage {
  private session: PersistedSession | undefined;
  async load(): Promise<PersistedSession | undefined> {
    return this.session;
  }
  async save(session: PersistedSession): Promise<void> {
    this.session = session;
  }
  async clear(): Promise<void> {
    this.session = undefined;
  }
}
